/**
 * The ghost cast — one archetype per kingdom, and the whole of what a human
 * writes for the bestiary.
 *
 * `packages/world/src/ghosts.ts` derives 244 creatures from the misconception
 * catalog, the curriculum and the world; what it cannot derive is what a
 * mistake *looks like* in a given kingdom. That is here, and it is deliberately
 * ten short entries rather than a file with a name for every error: the ghost's
 * noun comes from the curriculum's own concept title ("The Wraith of Common
 * Denominators"), so the cast supplies costume and nothing else.
 *
 * **The tone is the one `docs/rpg.md` §43 asks for and §12 insists on.** These
 * are a student's own mistakes walking around, and a mistake is not a moral
 * failure. So nothing here is a demon, nothing is cruel, and nothing is named
 * for stupidity — they are pests, tricksters and things that got the wrong end
 * of an idea, which is what a misconception actually is. A child who meets the
 * Miser of Order of Operations should want to catch it, not feel accused by it.
 *
 * **Forms read as `The ${form} of ${title}`.** That constraint is why they are
 * all singular nouns that take "of" cleanly. Which form a ghost takes is
 * derived from its ward concept, so it keeps its name for ever without anything
 * being stored — add a form to a kingdom and some of its ghosts are renamed,
 * which is a content decision and not a migration.
 *
 * `validateGhosts` runs on every `npm run content:validate` and checks the
 * things that are easy to get wrong here: a kingdom with no archetype, a
 * duplicate form, and — the one that matters — that every one of the 550
 * catalog entries can still fill a seal with exactly one true statement.
 */

import type { GhostArchetype } from '@yuan-math/world';

/**
 * One per kingdom, in the world's own order.
 *
 * Three forms each. Two is the floor the validator enforces and three is what
 * keeps a kingdom's bestiary from reading as one creature repeated — with the
 * thinnest kingdom holding ten wards, three costumes spread them thinly enough
 * to tell apart and few enough that the kingdom still has a character.
 */
export const GHOST_ARCHETYPES: GhostArchetype[] = [
  {
    kingdomId: 'kingdom-starter-village',
    emoji: '🕯️',
    forms: ['Bogle', 'Imp', 'Lantern-Thief'],
    habitat: 'Small mischiefs of the lanes and the barn — they hide a carry, or borrow a remainder and forget to give it back.',
  },
  {
    kingdomId: 'kingdom-number',
    emoji: '🪙',
    forms: ['Miser', 'Tally-Wight', 'Coin-Shade'],
    habitat: 'Counters of things in the wrong order. They love a decimal point and move it when nobody is looking.',
  },
  {
    kingdomId: 'kingdom-fraction',
    emoji: '🍰',
    forms: ['Wraith', 'Halver', 'Crumb-Sprite'],
    habitat: 'They cut the cake into pieces of different sizes and then insist on counting them together.',
  },
  {
    kingdomId: 'kingdom-algebra',
    emoji: '🎭',
    forms: ['Cipher', 'Riddler', 'Mask'],
    habitat: 'Things that hide behind a letter. They will happily do the right operation to one side of an equation only.',
  },
  {
    kingdomId: 'kingdom-geometry',
    emoji: '🗿',
    forms: ['Gargoyle', 'Stone-Shade', 'Warden'],
    habitat: 'Old, patient and literal-minded. They measure the wrong edge and are entirely satisfied with the answer.',
  },
  {
    kingdomId: 'kingdom-function',
    emoji: '⚙️',
    forms: ['Gremlin', 'Cog-Sprite', 'Mimic'],
    habitat: 'They live in the machinery and swap two wires — an input for an output, a slope for an intercept.',
  },
  {
    kingdomId: 'kingdom-linear-algebra',
    emoji: '🕸️',
    forms: ['Weaver', 'Lattice-Wight', 'Shadow'],
    habitat: 'They spin things in the wrong order and are quietly delighted that, here, the order changes the answer.',
  },
  {
    kingdomId: 'kingdom-statistics',
    emoji: '🌫️',
    forms: ['Rumour', 'Hearsay-Wisp', 'Fog'],
    habitat: 'They repeat what they heard about the data. They are certain, they are convincing, and they have not checked.',
  },
  {
    kingdomId: 'kingdom-trigonometry',
    emoji: '🌬️',
    forms: ['Echo', 'Wind-Wraith', 'Peak-Shade'],
    habitat: 'Voices off the mountain that come back almost right — the correct ratio, named after the wrong side.',
  },
  {
    kingdomId: 'kingdom-calculus',
    emoji: '💫',
    forms: ['Phantom', 'Limit-Shade', 'Vanisher'],
    habitat: 'They are there and then they are not. They will drop a term for being small and never mention it again.',
  },
];
